import { createSlice } from '@reduxjs/toolkit';
import { find, isEqual } from 'lodash';
import moment from 'moment';
import { proc } from '@aexae/js_tools';

import socket from '../../utils/socket';

// URLSearchParams
const initialArgs = { month: moment().format('YYYYMM'), activeSite: true };
const { month, siteId, activeSite } = proc.getQueryParams(initialArgs);

const initialState = {
  isLoading: false,
  isLoadingSite: true,
  error: false,
  hasNotifEnabled: false,
  refreshFilters: false,
  // URLSearchParams
  month: month.toString(),
  pickerDate: moment(month, 'YYYYMM').toDate(),
  siteId,
  // datas
  sites: [],
  siteSelected: {},
  hasRights: true,
  // api call props
  getSitesProps: null,
  // sort
  sortBy: ['createdAt'],
  sortOrders: ['asc'],
  keyToAvoid: null,
  // filter
  filterOptions: {
    companies: [],
    sectors: []
  },
  filterOptionsDatas: {
    companies: [],
    sectors: []
  },
  filters: {
    search: '',
    companies: [],
    sectors: [],
    hasUnassignedVacs: false,
    activeSite
  }
};

const slice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    // ERROR
    hasError(state, action) {
      state.error = action.payload;
      console.error(state.error);
      state.isLoading = false;
    },

    getSites(state, action) {
      const { props, sites, companiesIds, sectorsIds, currentSite, isDashboard } = action.payload;
      const { siteId, activeSite = state.filters.activeSite } = proc.getQueryParams();
      const hasClientRole = socket.user.role.includes('client');
      state.getSitesProps = props;
      // on ne change pas la date du picker et le mois si on est sur le dashboard
      if (!isDashboard) {
        state.pickerDate = moment(props.month, 'YYYYMM').toDate();
        state.month = props.month;
      }
      state.sites = sites;

      state.filterOptions.companies = companiesIds;
      state.filterOptions.sectors = sectorsIds;
      state.filterOptionsDatas.companies = companiesIds;
      state.filterOptionsDatas.sectors = sectorsIds;

      // suite au CW-2176 => on enlève seulement si pas dans les résultats...
      if (!props?.ignoreFilters) {
        Object.keys(state.filters).forEach((key) => {
          if (['companies', 'sectors'].includes(key)) {
            // si on a des filtres sélectionnés
            if (state.filters[key]?.length) {
              // on retire les données superflues => on ressort les ids des données reçue en requête
              const dataResult = [...action.payload[`${key}Ids`]].map((el) => el.id);
              // on met à jour les filtres
              state.filters[key] = state.filters[key].filter((data) => dataResult.includes(data.id));
            }
          }
        });
      }

      if (siteId) {
        const siteSelected = find(sites, (el) => el.id === siteId);
        if (siteSelected?.id) {
          state.siteSelected = siteSelected;
          state.hasNotifEnabled = siteSelected.hasAlertOnSite ?? false;
        }
      } else if (!state.siteSelected?.id) {
        const site = (!currentSite ? {} : sites?.find((site) => site.id === currentSite)) || {};
        if (hasClientRole) {
          state.siteSelected = sites[0] ?? {};
        } else {
          state.siteSelected = site || sites[0] || {};
        }
        state.siteId = site?.id ?? null;
        state.hasNotifEnabled = site?.hasAlertOnSite ?? false;
      }

      state.filters.activeSite = activeSite;
      state.isLoading = false;
      state.isLoadingSite = false;
      state.refreshFilters = !state.refreshFilters;
    },

    onSelectSite(state, action) {
      state.siteId = parseInt(action.payload, 10);
      const siteSelected = find(state.sites, (el) => el.id === parseInt(action.payload, 10));

      if (siteSelected) {
        state.siteSelected = siteSelected;
        state.hasNotifEnabled = siteSelected.hasAlertOnSite ?? false;
      } else {
        state.siteSelected = initialState.siteSelected;
        state.hasNotifEnabled = false;
      }
    },

    /**
     * Met à jour l'état local de l'alerte pour un site donné.
     *
     * Cette action modifie à la fois :
     * - le champ `hasAlertOnSite` dans la liste `sites[]`
     * - le champ `hasAlertOnSite` dans `siteSelected` si concerné
     * - la variable globale `hasNotifEnabled`
     *
     * @param {Object} action.payload
     * @param {number} action.payload.siteId - ID du site à modifier
     * @param {boolean} action.payload.enabled - Nouvelle valeur de l'alerte
     */
    updateSiteAlert(state, action) {
      const { siteId, enabled } = action.payload;

      // Mise à jour dans siteSelected si c'est le bon
      if (state.siteSelected?.id === siteId) {
        state.siteSelected.hasAlertOnSite = enabled;
        state.hasNotifEnabled = enabled;
      }

      // Mise à jour dans la liste
      const site = state.sites.find((s) => s.id === siteId);
      if (site) {
        site.hasAlertOnSite = enabled;
      }
    },

    setSiteSelected(state, action) {
      state.siteSelected = action.payload;
      state.hasNotifEnabled = action.payload.hasAlertOnSite ?? false;
    },

    setNotification(state, action) {
      state.hasNotifEnabled = action.payload;
    },

    setDateSites(state, action) {
      state.pickerDate = action.payload;
      state.month = moment(state.pickerDate).format('YYYYMM');
    },

    setFilterToAvoid(state, action) {
      state.keyToAvoid = action.payload;
    },

    // SORT
    sortBySites(state, action) {
      // state.sortBy = action.payload;
      // if (state.sortOrders === 'desc') state.sortOrders = 'asc';
      // else state.sortOrders = 'desc';
      const idx = state.sortBy.findIndex((el) => el === action.payload);
      if (idx >= 0) {
        if (state.sortOrders[0] === 'desc') {
          state.sortBy.splice(0, 1);
          state.sortOrders.splice(0, 1);
        } else {
          state.sortOrders[0] = 'desc';
        }
      } else {
        state.sortBy[0] = action.payload;
        state.sortOrders[0] = 'asc';
      }
    },

    // FILTER
    filterSites(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    updateFilters(state, action) {
      state.filterOptions = action.payload;
    },

    // RESET FILTER
    resetFilterSites(state, action) {
      state.filters = initialState.filters;
      action.payload = initialState.filters;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setDateSites,
  setNotification,
  setSiteSelected,
  onSelectSite,
  sortBySites,
  filterSites,
  resetFilterSites,
  setFilterToAvoid,
  updateFilters,
  updateSiteAlert
} = slice.actions;

export function getSites(props = {}, reload = false, currentSite = null) {
  const {
    month = null,
    activeSites = false,
    checkRights = true,
    notInIds = null,
    clockingSiteEnabled = null,
    position = null,
    isDashboard = false
  } = props;

  return async (dispatch, getState) => {
    const { getSitesProps } = getState().sites;

    if (reload || !isEqual(props, getSitesProps)) {
      dispatch(slice.actions.startLoading());
    }

    try {
      const result = await socket.get('/sites', {
        month,
        activeSites,
        checkRights,
        notInIds,
        clockingSiteEnabled,
        position
      });
      dispatch(slice.actions.getSites({ ...result, props, currentSite, isDashboard }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.stopLoading());
    }
  };
}
